import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import {
  SearchFilter,
  FilterSelect,
  Pagination,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import { Search } from "MODULES/shared/components";
import { matchesBoolean, matchesString } from "LIB/filters";
import {
  AIRCRAFT,
  APU,
  ENGINE,
  LANDING_GEAR,
  LOT_TYPE,
  OTHER
} from "MODULES/Lots/constants";
import { properCase } from "LIB/formatters/text";
import New from "MODULES/Lots/Create/New";
import {
  CANCELLED,
  CLEAR_FILTERS_TEXT,
  INVOICED,
  RECEIVED
} from "APP_ROOT/constants";
import { BUYING_STORE_ID, TRACE_HOLD } from "MODULES/PurchaseOrders/constants";
import { APPROVED } from "MODULES/companies/constants";
import Table from "./Table";
import { lotsPagination } from "../pagination";
import useCurrentUser from "../../../hooks/useCurrentUser";

const defaultFilters = {
  approved: undefined,
  buying_store_id: undefined,
  cancelled: undefined,
  current_user: undefined,
  invoiced: undefined,
  looking_for: undefined,
  lot_type: undefined,
  no_quote: undefined,
  no_interest: undefined,
  offer: undefined,
  received: undefined,
  trace_hold: undefined
};

const filters = [
  {
    label: "My LPOs",
    filters: { current_user: true }
  },
  {
    label: "Received",
    filters: { received: true }
  },
  {
    label: "Not Received",
    filters: { received: false }
  },
  {
    label: "Cancelled",
    filters: { cancelled: true }
  },
  {
    label: "Not Cancelled",
    filters: { cancelled: false }
  },
  {
    label: "Invoiced",
    filters: { invoiced: true }
  },
  {
    label: "Not Invoiced",
    filters: { invoiced: false }
  },
  {
    label: "Synced with Accounting",
    filters: {
      accounting_synced: true
    }
  },
  {
    label: "Not Synced with Accounting",
    filters: {
      accounting_synced: false
    }
  },
  {
    label: "Open LPOs Awaiting Trace Pre-approval",
    filters: { trace_hold: true, received: false }
  },
  {
    label: "Open LPOs Awaiting Purchaser Limit Approval",
    filters: { approved: false }
  },
  {
    label: properCase(AIRCRAFT),
    filters: { ...defaultFilters, lot_type: AIRCRAFT }
  },
  {
    label: APU.toUpperCase(),
    filters: { lot_type: APU }
  },
  {
    label: properCase(ENGINE),
    filters: { lot_type: ENGINE }
  },
  {
    label: properCase(LANDING_GEAR),
    filters: { lot_type: LANDING_GEAR }
  },
  {
    label: properCase(OTHER),
    filters: { lot_type: OTHER }
  }
];

const filterTags = [
  {
    label: "My LPOs",
    key: "current_user"
  },
  {
    label: "OQs with Internal Notes",
    key: "has_internal_note"
  },
  {
    label: "Awaiting Approval",
    key: APPROVED,
    matchesTarget: matchesBoolean(false)
  },
  {
    label: properCase(AIRCRAFT),
    key: LOT_TYPE,
    matchesTarget: matchesString(AIRCRAFT)
  },
  {
    label: properCase(APU),
    key: LOT_TYPE,
    matchesTarget: matchesString(APU)
  },
  {
    label: properCase(ENGINE),
    key: LOT_TYPE,
    matchesTarget: matchesString(ENGINE)
  },
  {
    label: properCase(LANDING_GEAR),
    key: LOT_TYPE,
    matchesTarget: matchesString(LANDING_GEAR)
  },
  {
    label: properCase(OTHER),
    key: LOT_TYPE,
    matchesTarget: matchesString(OTHER)
  },
  {
    label: properCase(RECEIVED),
    key: RECEIVED,
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Received",
    key: RECEIVED,
    matchesTarget: matchesBoolean(false)
  },
  {
    label: properCase(CANCELLED),
    key: CANCELLED,
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Cancelled",
    key: CANCELLED,
    matchesTarget: matchesBoolean(false)
  },
  {
    label: properCase(INVOICED),
    key: INVOICED,
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Invoiced",
    key: INVOICED,
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Accounting Synced",
    key: "accounting_synced",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Synced w/ Accounting",
    key: "accounting_synced",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Trace Preapproval Required",
    key: TRACE_HOLD,
    matchesTarget: matchesBoolean(true)
  }
];

function LotsSearch() {
  const currentUser = useCurrentUser();

  const filterTagsWithLocations = currentUser.locations.map(l => ({
    label: `Owned by ${l.name}`,
    key: BUYING_STORE_ID,
    matchesTarget: matchesString(l.id)
  }));

  const filtersWithLocations = currentUser.locations.map((l, i) => [
    {
      label: `All Owned By ${l.name}`,
      filters: { buying_store_id: l.id }
    },
    {
      label: `Open LPOs owned by ${l.name}`,
      filters: {
        ...defaultFilters,
        received: false,
        cancelled: false,
        buying_store_id: l.id
      },
      hr: currentUser.locations.length - 1 === i
    }
  ]);

  return (
    <Search search={lotsPagination} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing an LPO #, vendor name or buyer..."
              />
              <FilterSelect
                {...searchProps}
                filters={filters.concat(...filtersWithLocations).concat({
                  label: CLEAR_FILTERS_TEXT,
                  filters: { ...defaultFilters }
                })}
                right
              />
              <New />
            </div>
          </div>
          <TitleWithFilters
            {...searchProps}
            title="Lot Orders"
            filters={filterTags.concat(filterTagsWithLocations)}
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}

LotsSearch.propTypes = {
  pushHistory: PropTypes.func.isRequired
};

export default connect(undefined, { pushHistory: push })(LotsSearch);



// WEBPACK FOOTER //
// ./app/modules/Lots/Search/Lots.jsx